import { Link } from "react-router-dom";
import HeroCarousel from "@/components/feature/HeroCarousel";
import MediaCard from "@/components/base/MediaCard";
import NewsCard from "@/components/base/NewsCard";
import SectionHeading from "@/components/base/SectionHeading";
import {
  getFeaturedMedia,
  getLatestMovies,
  getHotTvShows,
  getLatestNews,
  movies,
  tvShows,
  newsList,
} from "@/lib/catalog";

const categoryEntries = [
  {
    to: "/category/movie",
    icon: "ri-film-line",
    title: "电影",
    desc: "最新上映与经典佳片",
    count: movies.length,
    tone: "primary" as const,
  },
  {
    to: "/category/tv",
    icon: "ri-tv-2-line",
    title: "电视剧",
    desc: "热播剧集与精品好剧",
    count: tvShows.length,
    tone: "accent" as const,
  },
  {
    to: "/category/news",
    icon: "ri-newspaper-line",
    title: "资讯",
    desc: "行业动态与深度报道",
    count: newsList.length,
    tone: "secondary" as const,
  },
];

export default function Home() {
  const featured = getFeaturedMedia(5);
  const latestMovies = getLatestMovies(8);
  const hotTv = getHotTvShows(8);
  const latestNews = getLatestNews(5);

  return (
    <div className="w-full">
      <HeroCarousel items={featured} />

      {/* 分类快捷入口 */}
      <section className="w-full px-4 md:px-6 -mt-10 relative z-20">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
          {categoryEntries.map((entry) => {
            const toneBg =
              entry.tone === "primary"
                ? "bg-primary-500"
                : entry.tone === "accent"
                ? "bg-accent-500"
                : "bg-secondary-500";
            return (
              <Link
                key={entry.to}
                to={entry.to}
                className="group flex items-center gap-4 p-5 bg-background-50 border border-background-200 rounded-lg hover:border-primary-300 transition-colors cursor-pointer"
              >
                <span className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${toneBg}`}>
                  <i className={`${entry.icon} text-2xl text-background-50`} />
                </span>
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-foreground-950 group-hover:text-primary-500 transition-colors">
                    {entry.title}
                  </h3>
                  <p className="text-xs text-foreground-500 truncate">{entry.desc}</p>
                </div>
                <span className="ml-auto text-xs font-semibold text-foreground-400">
                  {entry.count}+
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* 最新电影 */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <SectionHeading
          eyebrow="Latest Movies"
          title="最新电影推荐"
          description="汇集近期上映与更新的热门电影，涵盖多种类型供你挑选。"
          moreHref="/category/movie"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {latestMovies.map((item) => (
            <MediaCard key={item.id} item={item} />
          ))}
        </div>
      </section>

      {/* 热门电视剧 */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50 border-y border-background-200">
        <SectionHeading
          eyebrow="Hot Series"
          title="热门电视剧"
          description="根据观看热度精选的高人气剧集，追剧不迷路。"
          moreHref="/category/tv"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {hotTv.map((item) => (
            <MediaCard key={item.id} item={item} />
          ))}
        </div>
      </section>

      {/* 影视资讯精选 */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <SectionHeading
          eyebrow="News & Stories"
          title="影视资讯精选"
          description="行业动态、剧集资讯与深度报道，掌握影视圈最新动向。"
          moreHref="/category/news"
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {latestNews.map((item) => (
            <NewsCard key={item.id} item={item} />
          ))}
        </div>
      </section>

      {/* 站点简介 */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-primary-900">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-widest uppercase text-accent-400 mb-3">
              <span className="w-4 h-0.5 bg-accent-400 inline-block" />
              About Madou
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-background-50 mb-4">
              一站式影视信息聚合平台
            </h2>
            <p className="text-sm md:text-base text-primary-100/90 leading-relaxed mb-4">
              麻豆网站致力于为影视爱好者提供丰富、实时更新的影视资源汇总与资讯报道。我们聚合电影、电视剧两大类别的内容信息，并持续跟踪最新影视动态，让你在第一时间了解行业变化与作品推荐。
            </p>
            <p className="text-sm text-primary-200/80 leading-relaxed mb-6">
              本站所有内容均为信息展示，方便用户浏览、筛选与深入了解影视作品，不提供在线播放、下载或会员服务。
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              了解麻豆网站
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { num: "24+", label: "精选影视作品" },
              { num: "10+", label: "影视资讯报道" },
              { num: "8", label: "覆盖地区与类型" },
              { num: "每日", label: "内容持续更新" },
            ].map((stat) => (
              <div key={stat.label} className="p-5 rounded-lg bg-primary-800/60 border border-primary-700">
                <p className="text-2xl md:text-3xl font-black text-accent-400">{stat.num}</p>
                <p className="mt-1 text-xs md:text-sm text-primary-100">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}